/**
 * ------ maintenance history ------
 * 05/24/2019 Bowen Li: changed the float menu showing
 * 08/27/2019 Ella  Ma: grid multi or tiered sorting
 * 09/05/2019 Yoyo Fang: service-lize column chart tile
 * 11/10/2019 Yoyo Fang: service-lize line chart tile
 * 11/11/2019 Yoyo Fang: service-lize metadata tile and fix multiple query called on refresh
 * 11/11/2019 Yoyo Fang: service-lize value tile
 * 11/14/2019 Yoyo Fang: simplify "_executeQuery“ from rxjs pipe style to plain "_reloadWidget"
 * 12/09/2019 Yoyo Fang: add tile initialization code for existing and newly added tiles.
 * 01/08/2020 Ella Ma: code refactor save dashboard.
 */

import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin, of, Subscription, Observable } from 'rxjs';
import { switchMap, map, take, filter, delay, catchError, skip } from 'rxjs/operators';
import * as _ from 'lodash';

import {
    TamDashboardOptions, TamDashboardActions, TamDashboardTileType, TamDashboardEvents, TileIcons, TamTileSettingsEvents
} from '../../../components/tam-dashboard/tam-dashboard.model';
import { AppState } from '../../../redux';
import { ScreenActionTypes } from '../../../redux/actions/screen.actions';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { TileService } from '../../../tamalelibs/services/tile.service';
import { SavedDashboard, ScreenFocusType } from '../../../models/saved-screen';
import { QueryService } from '../../../services/query.service';
import { BriefConfig } from '../../../widgets/widgets.model';
import { TileSettingEvents, TileOperations } from '../../../components/dashboard/tile-setting/tile-setting.model';
import { DashboardEditorSubChromeConfig, DashboardEditorSubChromeEventType, DashboardEditorSubChromeActionType } from '../../../components/tam-dashboard/dashboard-viewer/dashboard-editor-sub-chrome/dashboard-editor-sub-chrome.model';
import { LayoutService } from '../../../services/layout.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { DashboardService } from '../dashboard.service';
import { FloatMenuService } from '../../../services/float-menu.service';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { AlertButton, AlertBtnTypes } from '../../../widgets/alert-window/alert-window.model';
import { TileSettingWrapperComponent } from '../../../components/dashboard/tile-setting/tile-setting-wrapper.component';
import { SaveScreenDialogOperations } from '../../../components/save-screen-dialog/save-screen-dialog.view-model';
import { getFocusStateSelector } from '../../../redux/reducers/dashboard.reducer';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { TileSettingService } from '../../../services/tile-setting.service';
import { TileModel } from '../../../tamalelibs/models/tile.model';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { TileColumn } from '../../../tamalelibs/models/tile-column.model';
import { TamDashboardColumnChartTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-column-chart-tile/tam-dashboard-column-chart-tile.config';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TamDashboardGridTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-grid-tile/tam-dashboard-grid-tile.config';
import { TamDashboardLineChartTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-line-chart-tile/tam-dashboard-line-chart-tile.config';
import { TamDashboardMetadataTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-metadata-tile/tam-dashboard-metadata-tile.config';
import { TamDashboardValueTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-value-tile/tam-dashboard-value-tile.config';
import { TamDashboardPieChartTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-pie-chart-tile/tam-dashboard-pie-chart-tile.config';
import { TamDashboardPopUpService } from '../../../components/tam-dashboard/tam-dashboard-popup.service';
import { SystemActionTypes } from '../../../tamalelibs/redux/actions/system.actions';
import { TamDashboardThreadTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-thread-tile/tam-dashboard-thread-tile.config';
import { TamDashboardRichTextTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-rich-text-tile/tam-dashboard-rich-text-tile.config';
import { Base64Service } from '../../../tamalelibs/services/base64.service';
import { DashboardTileService } from '../../../components/tam-dashboard/dashboard-tile.service';
import { TamDashboardMapTileConfiguration } from '../../../components/tam-dashboard/tam-dashboard-map-tile/tam-dashboard-map-tile.config';
import { ContactViewModel } from '../../../tamalelibs/components/contact.view-model';
import { EntityViewModelForWeb } from '../../../components/entity-dialog/entity-view-web.model';
import { ContactService } from '../../../tamalelibs/services/contact.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';

@Component({
    selector: 'tam-dashboard-editor',
    templateUrl: './dashboard-editor.component.html',
    styleUrls: ['./dashboard-edit.component.scss']
})
export class DashboardEditorComponent implements OnInit, OnDestroy {

    @ViewChild('tileConfigComp', { static: false }) tileConfigComp: TileSettingWrapperComponent;

    dashboardOptions: TamDashboardOptions = new TamDashboardOptions(false);
    dashboardEditorSubChromeConfig: DashboardEditorSubChromeConfig = new DashboardEditorSubChromeConfig();
    isPageLoading: boolean;
    tileSettingConfig: BriefConfig = new BriefConfig();
    screenInfo: any;

    private _activeTileName: string;
    private _activeTileId: string;
    private _activeVisualizationType: number;
    private _focusedEntity: any;
    private _source = {};
    private _screenData: SavedDashboard;
    private _destroySubscriptions: Subscription[] = [];
    private _opType;
    private _operateIcons = [TileIcons.COPY, TileIcons.DELETE, TileIcons.REFRESH, TileIcons.HIDE_BORDER, TileIcons.HIDE_TITLE];

    constructor(
        private _alertWindow: AlertWindowService,
        private _dashboardService: DashboardService,
        private _dashboardTileService: DashboardTileService,
        private _entityService: EntityService,
        private _layoutService: LayoutService,
        private _queryService: QueryService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _screenService: ScreenService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _tileService: TileService,
        private _toast: ToastService,
        private _floatMenuService: FloatMenuService,
        private _deviceService: DeviceDetectorService,
        private _utils: UtilsService,
        private _dashboardPopUpService: TamDashboardPopUpService,
        private _base64: Base64Service,
        private _contactViewModel?: ContactViewModel,
        private _entityViewModel?: EntityViewModelForWeb,
        private _contactService?: ContactService,
    ) { }

    ngOnInit() {
        this._floatMenuService.floatMenuBottomSubject$.next(false);
        this._destroySubscriptions.push(
            this.tileSettingConfig.feedbackSubject$.subscribe(event => this._tileSettingEventHandler(event)),
            this.dashboardOptions.feedbackSubject$.subscribe(event => this._dashboardEventHandler(event)),
            this.dashboardEditorSubChromeConfig.actions$.subscribe(event => this._editSubChromeEventHandler(event)),
            this._route.paramMap.pipe(
                skip(1)
            ).subscribe((router) => {
                this._resetDashbaordPage(router);
            })
        );
        const dashboardId = this._route.snapshot.params.id;
        this.initComponent(dashboardId);
        this._activeTileId = this._route.snapshot.queryParams.id;
        this.dashboardOptions.icons = this._operateIcons;
    }

    ngOnDestroy(): void {
        this._clearFocusValue();
        this._floatMenuService.floatMenuBottomSubject$.next(true);
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    initComponent(screenId: string) {
        this.isPageLoading = true;
        this._screenService.getById(screenId).pipe(
            take(1),
            switchMap((screen: SavedDashboard) => {
                if (!screen.editable) {
                    this._router.navigate(['error']);
                    return of(null);
                }
                // set page title
                this.screenInfo = {
                    url: this._route.snapshot.url,
                    name: this._route.snapshot.url[1].path.toUpperCase() === SaveScreenDialogOperations.COPY ?
                        `Copy of ${screen.name}` : screen.name,
                    type: 'DASHBOARD_EDITOR',
                    id: screenId
                };
                this._opType = this._route.snapshot.url[1].path.toUpperCase();
                // this._route.snapshot.url[1].path = create || edit || copy
                this.dashboardEditorSubChromeConfig.events$.next({
                    type: DashboardEditorSubChromeEventType.changeInfo,
                    payload: { ...this.screenInfo, opType: this._opType }
                });
                this._store.dispatch({
                    type: ScreenActionTypes.SET_VALUE,
                    payload: this.screenInfo
                });
                if (screen.configuration.widgets) {
                    screen.configuration.widgets = screen.configuration.widgets.filter(item => item.tileId);
                } else {
                    /* fix bug 1.0 - dashboard no tile save widgets value is null,
                    * 2.0 add tile will console error on line this._screenData.configuration.widgets.find
                    */
                    screen.configuration.widgets = [];
                }
                if (screen.configuration.widgets.length === 0) {
                    this.isPageLoading = false;
                    this._screenData = screen;
                    this.dashboardEditorSubChromeConfig.events$.next({
                        type: DashboardEditorSubChromeEventType.changeData,
                        payload: screen
                    });
                    // when create a new dashboard on this page, need reset the data
                    // MUST first render dashboard items before send request to load data
                    this.dashboardOptions.actionSubject$.next({
                        type: TamDashboardActions.RESET,
                        payload: []
                    });
                    return of(null);
                } else {
                    // load tile configuration from server
                    const observables = [];
                    screen.configuration.widgets.forEach(element => {
                        observables.push(this._tileService.getById(element.tileId));
                    });
                    return forkJoin(observables).pipe(
                        map(tiles => {
                            const length = screen.configuration.widgets.length;
                            for (let i = 0; i < length; i++) {
                                screen.configuration.widgets[i].tile = tiles[i];
                            }
                            return screen;
                        })
                    );
                }
            }),
            filter(screen => screen),
            map(screen => {
                // init tiles without data
                this._screenData = screen;
                this._resetScreenData();
                this.dashboardEditorSubChromeConfig.events$.next({
                    type: DashboardEditorSubChromeEventType.changeData,
                    payload: screen
                });
                if (screen.configuration.focus.entityTypes && screen.configuration.focus.entityTypes.length > 0) {
                    this._source['entityTypes'] = screen.configuration.focus.entityTypes;
                }
                return this._dashboardService.formatDashboardScreen(screen, this.dashboardOptions);
            }),
            delay(0), // use delay to make sure every tile was initialized
            switchMap((screen) => {
                // setActiveTile must before 'LOAD_SETTING'
                if (this._activeTileId) {
                    this._setActiveTile();
                }
                const observables = [];
                if (this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                    const storeFocusValue = this._storeQuerier.queryBySelector(getFocusStateSelector);
                    if (storeFocusValue && storeFocusValue.shortName) {
                        observables.push(this._setValueForFocus(storeFocusValue));
                    }
                }
                if (observables.length === 0) {
                    return of({ screen, focusedEntity: null });
                } else {
                    return forkJoin(observables).pipe(
                        map((focusedEntity) => ({ screen, focusedEntity })),
                    );
                }
            }),
            switchMap(({ screen, focusedEntity }) => {
                if (focusedEntity && focusedEntity.length > 0) {
                    this._focusedEntity = focusedEntity[0];
                }
                this.isPageLoading = false;
                const observables = [];
                const typedScreen = (screen as SavedDashboard);
                typedScreen.configuration.widgets.forEach(element => {
                    if (element.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
                        typedScreen.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                        if (focusedEntity && focusedEntity.length > 0) {
                            this.dashboardEditorSubChromeConfig.events$.next({
                                type: DashboardEditorSubChromeEventType.setFoucsValue,
                                payload: focusedEntity[0]
                            });
                            this._queryService.prepareRelationshipFocusForQuery(focusedEntity[0].id, element.tile.focus.relationshipTypes)
                                .pipe(take(1))
                                .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds, true));
                        } else {
                            const config = this._getTileConfigurationByType(element.tile.visualization.type);
                            this._initTile(element.id, { config: config, widget: element });
                            this._setTileNoData(element);
                            // this._toggleLoadingStatus(element.id, false);
                        }
                    } else {
                        this._queryService.prepareFocusForQuery(element.tile, typedScreen.configuration.focus)
                            .pipe(take(1))
                            .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds, true));
                    }
                });
                return forkJoin(observables);
            }),
            catchError((e: any) => {
                this._router.navigate(['error']);
                return of(null);
            })
        ).subscribe();
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        $event.returnValue = true;
    }

    addTile(tileType) {
        this.dashboardOptions.actionSubject$.next({
            type: TamDashboardActions.ADD_ITEM,
            payload: tileType
        });
    }

    private _clearFocusValue() {
        if (this.dashboardEditorSubChromeConfig.clearStoreFocusValue) {
            this._store.dispatch({
                type: DashboardActionTypes.SET_FOCUS_VALUE,
                payload: null
            });
        }
    }

    private _changeTile(tileSetting, event) {
        if (tileSetting && tileSetting.tile) {
            this._activeTileName = tileSetting.tile.tileName;
            this._activeVisualizationType = tileSetting.tile.visualization.type;
        }
        this._activeTileId = event.id;
        if (tileSetting && tileSetting.tile) {
            tileSetting.tile.columns.forEach((item: TileColumn) => {
                if (item.settings && item.settings.negatives) {
                    // Handle 1.0 bug. "negatives" in 1.0 may be number, may be string. Web 2.0 will always be number.
                    item.settings.negatives = +item.settings.negatives;
                }
            });
        }
        // send tile setting to child component.
        this.tileSettingConfig.actionSubject$.next({
            type: TamTileSettingsEvents.LOAD_SETTING,
            id: event.id,
            payload: {
                tileSetting: tileSetting && tileSetting.tile ? _.cloneDeep(tileSetting.tile) : null,
                screenFocusType: this._screenData.configuration.focus.type,
                operateTile: event.operateTile
            },
        });
        this._setTileActive(event.id);
    }

    private _copySingleTile(id: string) {
        const index = this._screenData.configuration.widgets.findIndex(item => item.id === id);
        if (index !== -1) {
            const copyTile = this._screenData.configuration.widgets[index];
            const newTileConfig = _.cloneDeep(copyTile.tile);
            newTileConfig.tileName = `Copy of ${newTileConfig.tileName}`;
            delete newTileConfig.id;
            this.dashboardOptions.actionSubject$.next({
                type: TamDashboardActions.COPY_ITEM,
                tile: newTileConfig,
                copyTileId: copyTile.id,
                payload: {
                    cols: copyTile.width,
                    rows: copyTile.height,
                    name: newTileConfig.tileName,
                    type: newTileConfig.visualization.type,
                    icons: this._operateIcons
                }
            });
        }

    }

    private _deleteSingleTile(id: string) {
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom('Delete confirmation required', ['Are you sure you want to delete this tile?'], cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    if (this._activeTileId === id) {
                        this._activeTileId = null;
                        this.tileConfigComp.currentTileId = null;
                        this._setTileActive(null);
                    }
                    this.dashboardOptions.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.DELETE_ITEM,
                    });
                    this.tileConfigComp.setModifyValue('');
                }
                subscription.unsubscribe();
            });
    }

    private _dashboardEventHandler(event) {
        if (event.type === TamDashboardEvents.ITEM_CLICKED) {
            const tileSetting = this._screenData.configuration.widgets.find(element => element.id === event.id);
            this.tileSettingConfig.actionSubject$.next({
                type: TamTileSettingsEvents.CHANGE_ITEM_CLICK,
                payload: {
                    tileSetting,
                    event
                }
            });
        } else if (event.type === TamDashboardEvents.ITEM_CHANGED) {
            const tileSetting = this._screenData.configuration.widgets.find(element => element.id === event.id);
            if (tileSetting) {
                tileSetting.positionX = event.payload.positionX;
                tileSetting.positionY = event.payload.positionY;
                tileSetting.width = event.payload.width;
                tileSetting.height = event.payload.height;
            } else {
                this._screenData.configuration.widgets.push(event.payload);
                this._activeTileId = event.payload.id;
                if (event.payload.tile) {  // copy tile
                    this._setActiveTile(TileOperations.COPY);
                    if (event.payload.tile.visualization.type === TamDashboardTileType.GRID) {
                        this._refreshCopyGridTile(event.payload);
                    } else {
                        this._refreshSingleTile(event.payload.id, false, true, false, true);
                    }
                } else {  // add new tile
                    this._setActiveTile(TileOperations.ADD);
                    if (event.payload && event.payload.type === TamDashboardTileType.EDITOR) {
                        // TODO: remove this later when implement story, for DEMO purpose only
                        const widget = this._screenData.configuration.widgets.filter(element => element.id === event.payload.id)[0];
                        widget.tile = { visualization: { type: 8 } };
                    }
                }
            }
        } else if (event.type === TamDashboardEvents.ITEM_ICON_CLICK) {
            /**
             * refresh - have unsaved changes, show dialog. ['Go Back'] - close alert, ['Discard'] - change focus;
             *           has not unsaved changes, default action.
             * delete - Whether it has changed，
             *          if trigger click self, delete tile,
             *          else trigger click other tile,
             *               if have unsaved changes, show dialog.
             *                  ['Go Back'] - close alert, ['Discard'] - change focus.
             *               else have not unsaved changes, delete tile
             * copy - have unsaved changes, show dialog.['Go Back'] - close alert, ['Discard'] - change focus;
             *        has not unsaved changes, default action.
             */
            if (event.payload.icon === TileIcons.DELETE.icon && this._activeTileId && event.payload.id &&
                this._activeTileId === event.payload.id) {
                this._singleTileOperate(event);
            } else if (this.tileConfigComp && this.tileConfigComp.getModifyValue()) {
                // refresh tile and copy tile, if current tile has changed, show alert
                const subscription = this._alertWindow.warn('You have unsaved changes',
                    ['Are you sure you want to discard the changes?',
                        'Please click "Apply & Preview" to apply them']).subscribe((result: boolean) => {
                            if (result) {
                                // if current tile is a not preview tile and change focus, should delete the new tile
                                const currentTile = this._screenData.configuration.widgets.find(element => element.id === this._activeTileId);
                                if (currentTile && !currentTile.tileName) {
                                    this.tileConfigComp.delNewTile();
                                }
                                const tileSetting = this._screenData.configuration.widgets.find(element => element.id === event.payload.id);
                                this._changeTile(tileSetting, event.payload);
                            }
                            subscription.unsubscribe();
                        });
            } else {
                this._singleTileOperate(event);
            }
        } else if (event.type === TamDashboardEvents.ITEM_DELETE) {
            const index = this._screenData.configuration.widgets.findIndex(item => item.id === event.id);
            if (index !== -1) {
                this._screenData.configuration.widgets.splice(index, 1);
                this.dashboardEditorSubChromeConfig.events$.next({
                    type: DashboardEditorSubChromeEventType.changeData,
                    payload: this._screenData
                });
            }
        } else if (event.type === TamDashboardEvents.ON_COLUMN_ORDER_CHANGED) {
            const index = this._screenData.configuration.widgets.findIndex(item => item.id === event.id);
            if (index > -1) {
                this._screenData.configuration.widgets[index].tile.columns = TileSettingService.sortColumnsByIdList(this._screenData.configuration.widgets[index].tile.columns, event.payload.columnIds);
                if (this._activeTileId === event.id) {
                    this.tileSettingConfig.actionSubject$.next(event);
                }
            }
        } else if (event.type === TamDashboardEvents.TILE_SETTINGS_LOADED) {
            this._returnLatestSettings(event);
        } else if (event.type === TamDashboardEvents.SET_VALUE) {
            this.tileConfigComp.setModifyValue(event.payload);
        } else if (event.type === TamDashboardEvents.RESIZE_COLUMN_WIDTH) {
            this._updateScreenDataItemColumnWidthType(event.id);
            this.tileSettingConfig.actionSubject$.next(event);
        }
    }

    private _editSubChromeEventHandler(event) {
        if (event.type === DashboardEditorSubChromeActionType.refreshScreen) {
            this._refreshScreenData(event);
        } else if (event.type === DashboardEditorSubChromeActionType.onFinderValueChange) {
            this._onFinderValueChange(event.payload);
        } else if (event.type === DashboardEditorSubChromeActionType.saveGridLayout) {
            this._saveGridLayout(event.payload.id, event.payload.tile);
        } else if (event.type === DashboardEditorSubChromeActionType.onSave) {
            this._saveDashboard();
        }
    }

    private _saveScreenDataAPI() {
        const saveSuccessHandler = () => {
            this.dashboardEditorSubChromeConfig.events$.next({
                type: DashboardEditorSubChromeEventType.setLoadingStatus,
                payload: false
            });
            this.dashboardEditorSubChromeConfig.changeRouterBySelf = true;
            let message;
            if (this._opType === SaveScreenDialogOperations.CREATE || this._opType === SaveScreenDialogOperations.COPY) {
                message = `${this._screenData.name} is created successfully.`;
            } else {
                message = `${this._screenData.name} successfully updated.`;
            }
            this._toast.notify({
                message: message,
                style: NotificationStyles.Success
            });
            this.dashboardEditorSubChromeConfig.events$.next({
                type: DashboardEditorSubChromeEventType.setFocusValueStore
            });
            this._router.navigate(['dashboard', this.screenInfo.id]);
        };
        const observables = [];
        this._screenData.configuration.widgets.forEach(item => {
            item.tile.columns.forEach((column) => {
                column.title = column.title.trim();
            });
            if (item.tile && item.tile.id && this._opType !== SaveScreenDialogOperations.COPY) {
                observables.push(this._tileService.updateTile(item.tile));
            } else {
                observables.push(this._tileService.insertTile(item.tile));
            }
        });
        if (observables.length > 0) {
            forkJoin(observables).pipe(
                map((data: any) => {
                    data.forEach((item, index) => {
                        if (this._screenData.configuration.widgets[index].tile) {
                            this._screenData.configuration.widgets[index].tile.id = item.id;
                        }
                        this._screenData.configuration.widgets[index].tileId = item.id;
                    });
                    this._screenData.configuration.widgets.forEach(item => {
                        if (item.tile.visualization.type === TamDashboardTileType.GRID) {
                            this.dashboardOptions.actionSubject$.next({
                                type: DashboardEditorSubChromeActionType.saveGridLayout,
                                payload: item
                            });
                        }
                    });
                }),
                switchMap(() => {
                    this._screenData.configuration.widgets = this._screenData.configuration.widgets.filter(item => item.tileId).map(item => ({
                        height: item.height,
                        id: item.id,
                        positionX: item.positionX,
                        positionY: item.positionY,
                        tileId: item.tileId,
                        width: item.width
                    }));
                    return this._screenService.update(this._screenData);
                }),
                take(1)
            ).subscribe(() => {
                saveSuccessHandler();
            });
            return;
        }
        this._screenService.update(this._screenData).pipe(
            take(1)
        ).subscribe(() => {
            saveSuccessHandler();
        });
        return;
    }

    private _onSave() {
        if (this._opType === SaveScreenDialogOperations.COPY) {
            this._screenData.name = this.screenInfo.name;
            const screenData = _.cloneDeep(this._screenData);
            delete screenData.id;
            screenData.configuration.widgets = [];
            screenData.name = this.screenInfo.name;
            this._screenService.create(screenData).pipe(
                take(1)
            ).subscribe(response => {
                this.screenInfo.id = this._screenData.id = response.id;
                this._store.dispatch({
                    type: SystemActionTypes.ADD_SCREEN,
                    payload: this._screenData,
                });
                this._saveData();
            }, response => {
                if (response.message.includes('ERR_NAME_EXISTED')) {
                    this._toast.notify({
                        message: StringLiteralsPipe.translate('general.name_already_exist'),
                        style: NotificationStyles.Info
                    });
                }
            });
        } else {
            this._saveData();
        }
    }

    private _saveDashboard() {
        this.dashboardEditorSubChromeConfig.events$.next({
            type: DashboardEditorSubChromeEventType.setLoadingStatus,
            payload: true
        });
        if (this.tileConfigComp && this.tileConfigComp.currentTileId) {
            if (this.tileConfigComp.validateSettings()) {
                const isModify = this.tileConfigComp.getModifyValue();
                if (isModify) {
                    this.dashboardOptions.feedbackSubject$.pipe(
                        filter(event => event.type === TamDashboardActions.SET_TILE_PREVIEW_STATUS_SUCCESS),
                        take(1),
                    ).subscribe(events => {
                        // When modifying tile type, you can not directly click save to save, and add settimeout to ensure that you can save after preview execution
                        setTimeout(() => {
                            this._onSave();
                        }, 1000);
                    });
                    this.tileConfigComp.preview();
                } else {
                    this._onSave();
                }
            } else {
                this.dashboardEditorSubChromeConfig.events$.next({
                    type: DashboardEditorSubChromeEventType.setLoadingStatus,
                    payload: false,
                });
            }
        } else {
            this._onSave();
        }
    }

    private _saveData() {
        const gridLayoutObservales: Observable<any>[] = [];
        const widgets = this._screenData.configuration.widgets.filter(item => !item.notPreview);
        this._screenData.configuration.widgets = widgets;
        /** if visualization.type === TamDashboardTileType.GRID, first get grid layout */
        this._screenData.configuration.widgets.forEach(item => {
            if (item.tile.visualization.type === TamDashboardTileType.GRID) {
                gridLayoutObservales.push(this._dashboardService.getGridLayoutService(item.id, this.dashboardOptions.actionSubject$));
            }
        });
        if (gridLayoutObservales.length > 0) {
            forkJoin(gridLayoutObservales).pipe(
                map((data) => {
                    data.forEach(item => {
                        const widget = this._screenData.configuration.widgets.filter(tile => tile.id === item.payload.id);
                        const layoutColumns = [];
                        /*
                        * line 564-576 fix bug [TAM-28902],
                        * when click 'Apply&Preview', the grid has not get new layout data,
                        * then click save dashboard button, the layout and tile setting column not keep sync
                        */
                        widget[0].tile.columns.forEach(column => {
                            const index = item.payload.layout.columns.findIndex(col => col.name === column.guid);
                            if (index === -1) {
                                layoutColumns.push({
                                    name: column.guid,
                                    hide: column.hidden,
                                    width: null,
                                    pinned: null
                                });
                            } else {
                                layoutColumns.push(item.payload.layout.columns[index]);
                            }
                        });
                        item.payload.layout.columns = layoutColumns;
                        widget[0].tile.layout = item.payload.layout;
                    });
                    return of(this._screenData);
                })
            ).subscribe(() => {
                this._saveScreenDataAPI();
            });
        } else {
            this._saveScreenDataAPI();
        }
    }

    private _getTileConfigurationByType(type: TamDashboardTileType): any {
        switch (type) {
            case TamDashboardTileType.PIE_CHART:
                return new TamDashboardPieChartTileConfiguration(this._dashboardTileService, this._dashboardService);
            case TamDashboardTileType.COLUMN_CHART:
            case TamDashboardTileType.BAR_CHART:
                return new TamDashboardColumnChartTileConfiguration(this._dashboardTileService, this._dashboardService, null,
                    this._dashboardPopUpService, type === TamDashboardTileType.BAR_CHART);
            case TamDashboardTileType.LINE_CHART:
                return new TamDashboardLineChartTileConfiguration(this._dashboardTileService, this._dashboardService);
            case TamDashboardTileType.VALUE:
                return new TamDashboardValueTileConfiguration(this._dashboardTileService);
            case TamDashboardTileType.METADATA:
                return new TamDashboardMetadataTileConfiguration(this._dashboardTileService);
            case TamDashboardTileType.NOTES:
                const noteConfig = new TamDashboardThreadTileConfiguration(this._store, this._dashboardTileService);
                noteConfig.clickable = false;
                return noteConfig;
            case TamDashboardTileType.EDITOR:
                const editorConfig = new TamDashboardRichTextTileConfiguration(this._base64, this._store, this._dashboardTileService);
                editorConfig.readonly = false;
                return editorConfig;
            case TamDashboardTileType.MAP:
                return new TamDashboardMapTileConfiguration(this._store, this._dashboardTileService, this._contactViewModel, this._entityService, this._entityViewModel, this._contactService);
            case TamDashboardTileType.GRID:
            default:
                return new TamDashboardGridTileConfiguration(this._dashboardService, this._deviceService,
                    this._utils, this._dashboardTileService, this._layoutService, this._store, this._storeQuerier);
        }
    }

    private _reloadWidget(widget: any, focusedEntityIds, initTile = false) {
        try {
            // per tile type, initialize tile with configuration
            if (initTile) {
                const config = this._getTileConfigurationByType(widget.tile.visualization.type);
                this._initTile(widget.id, { config: config, widget: widget });
            }
            if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && this._screenData.configuration.focus.includeSpecificEntityItself) {
                focusedEntityIds = focusedEntityIds + ',' + this._screenData.configuration.focus.entityId;
            } else if (widget.tile.focus.includeSpecificEntityItself) {
                focusedEntityIds = focusedEntityIds + ',' + widget.tile.focus.entityId;
            }
            if (focusedEntityIds === '') {
                this._setTileNoData(widget);
                return;
            }

            // reload the tile
            // for grid wait for ready, initialization includes reloading layout
            // so if it is not inited, reload layout first
            if (widget.tile.visualization.type === TamDashboardTileType.GRID) {
                if (!initTile) {
                    this._refreshGrid(widget, focusedEntityIds);
                } else {
                    this.dashboardOptions.feedbackSubject$.pipe(
                        filter((event) => event.id === widget.id && event.type === TamDashboardEvents.SET_CONFIG_FINISHED),
                        take(1)
                    ).subscribe(() => {
                        this._loadData(widget.id, widget.tile, focusedEntityIds);
                    });
                }
            } else {
                this._loadData(widget.id, widget.tile, focusedEntityIds);
            }

            // label related
            if (widget.tile.visualization.type === TamDashboardTileType.PIE_CHART) {
                this._setPieChartLabel(widget.id, widget.tile.visualization.settings.displaySetting);
            }
            if (widget.tile.visualization.type === TamDashboardTileType.LINE_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.COLUMN_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.BAR_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.PIE_CHART) {
                const series = widget.tile.visualization.settings.series;
                const column = widget.tile.columns.filter(item => item.guid === series);
                if (column && column.length > 0) {
                    const dataType = column[0].dataType;
                    if (['number', 'currency', 'percent', 'ratio'].indexOf(dataType) !== -1) {
                        this._setLineOrColumnChartLabel(widget.id, {
                            dataType: dataType,
                            settings: column[0].settings
                        });
                    }
                }
            }
            this._setTileConfig(widget.id, widget.tile);
        } catch (e) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: e.message,
            });
            this._setTileNoData(widget);
            this._toggleLoadingStatus(widget.id, false);
        }
    }

    private _onFinderValueChange(value: Array<EntityBrief>) {
        this._focusedEntity = value;
        this._screenData.configuration.widgets
            .filter(element => element.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL)
            .forEach(element => {
                if (!value || value.length === 0) {
                    this._setTileNoData(element);
                    return;
                }
                this._toggleLoadingStatus(element.id, true);
                // There's no case in which the one-degree is enabled and tiles are added relationship type filters
                // So ignore the relationship type config for any tile when the dashboard has been enabled one-degree
                // The length of value is one means that the one degree is disabled on current dashboard.
                const focusedEntity$ = this._queryService.prepareRelationshipFocusForQuery(value.length === 1 ? value[0].id : value.map(ve => ve.id), element.tile.focus.relationshipTypes);
                if (element.tile.visualization.type === TamDashboardTileType.GRID) {
                    focusedEntity$
                        .pipe(take(1))
                        .subscribe((entityIds) => this._refreshGrid(element, entityIds));
                } else {
                    focusedEntity$
                        .pipe(take(1))
                        .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds));
                }
            });
    }

    private _refreshScreenData(data) {
        this.screenInfo.name = data.payload.name;
        this._screenData.name = data.payload.name;
        this._screenData.description = data.payload.description;
        this._screenData.accessList = data.payload.accessList;
        this._screenData.configuration.focus = data.payload.configuration.focus;
        this._screenData.isPublic = data.payload.isPublic;
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: this.screenInfo
        });
        this._screenData.configuration.focus.type = data.payload.configuration.focus.type;
        this._resetScreenData();
        this.tileSettingConfig.actionSubject$.next({
            type: TamTileSettingsEvents.CHANGE_SCREEN_FOCUS_TYPE,
            payload: data.payload.configuration.focus.type
        });
    }

    private _resetScreenData() {
        if (this._screenData.configuration.focus.type !== ScreenFocusType.ENABLE_FOCUS_CONTROL && this._screenData.configuration.widgets.length > 0) {
            // when dashboard change focus to not enabled focus, tile setting relationshipTypes will disabled, when user click save button directly, the relationshipTypes will be deleted.
            this._screenData.configuration.widgets.forEach(widget => {
                if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && widget.tile.focus.relationshipTypes) {
                    const len = widget.tile.focus.relationshipTypes.length;
                    if (len > 0) {
                        delete widget.tile.focus.relationshipTypes;
                    }
                }
            });
        }
    }

    // when create a new dashboard on this page, need reset the data
    private _resetDashbaordPage(router) {
        this._clearFocusValue();
        this._activeTileId = null;
        this.dashboardEditorSubChromeConfig.events$.next({
            type: DashboardEditorSubChromeEventType.setFoucsValue,
            payload: null
        });
        this.initComponent(router['params'].id);
        this._changeTile({}, {});
        // this._setTileActive(null);
    }

    private _refreshCopyGridTile(data) {
        this._dashboardService.getGridLayoutService(data.copyTileId, this.dashboardOptions.actionSubject$).pipe(take(1))
            .subscribe((event) => {
                const widget = this._screenData.configuration.widgets.filter(element => element.id === data.id)[0];
                widget.tile.layout = event.payload.layout;
                const config = new TamDashboardGridTileConfiguration(this._dashboardService, this._deviceService, this._utils, this._dashboardTileService, this._layoutService, this._store, this._storeQuerier);
                this._initTile(widget.id, { config: config, widget: widget });
                this.dashboardOptions.feedbackSubject$.pipe(
                    // tslint:disable-next-line: no-shadowed-variable
                    filter((event) => {
                        return event.id === widget.id && event.type === TamDashboardEvents.SET_CONFIG_FINISHED;
                    }),
                    take(1)
                ).subscribe(() => {
                    this._refreshSingleTile(data.id, true, true, false, true);
                });
            });
    }

    private _refreshSingleGridTile(tileId: string, tile: TileModel = null, resetColumns = false): void {
        this._dashboardService.getGridLayoutService(tileId, this.dashboardOptions.actionSubject$).pipe(take(1))
            .subscribe((event) => {
                const widget = this._screenData.configuration.widgets.filter(element => element.id === tileId)[0];
                const tempLayout = event.payload.layout;
                if (tile) {
                    const tempColumns: Array<{ hide: boolean, name: string }> = [];
                    tile.columns.forEach(column => {
                        if (!column.hidden) {
                            const tempIndex = tempLayout.columns.findIndex(item => item.name === column.guid);
                            if (tempIndex > -1) {
                                tempColumns.push(tempLayout.columns[tempIndex]);
                            } else {
                                tempColumns.push({
                                    hide: false,
                                    name: column.guid,
                                });
                            }
                        }
                    });
                    tempLayout.columns = tempColumns;
                }
                if (widget && widget.tile) {
                    widget.tile.layout = tempLayout;
                }
                // Apply&Preview layout change, when change focus, not show alert
                this.tileSettingConfig.actionSubject$.next({
                    type: TamTileSettingsEvents.CHANGE_TILE_DEFAULT_LAYOUT,
                    payload: event.payload.layout
                });
                this._refreshSingleTile(tileId, true, true, resetColumns);
            });
    }

    // Apply&Preview tile, Copy tile, Refresh tile, Update tile
    private _refreshSingleTile(id, notGetLayoutFromServer = false, notShowToast = false, resetColumns = false, fromCopy = false) {
        const widget = this._screenData.configuration.widgets.filter(element => element.id === id)[0];

        if (!widget || !widget.tile.tileName) {
            return;
        }
        if (widget.tile.tileName !== this._activeTileName) {
            this._setTileName(id, widget.tile.tileName);
        }
        // when Apply&Preview, wait for type change
        this.dashboardOptions.feedbackSubject$.pipe(
            filter(event => event.type === TamDashboardEvents.TYPE_CHANGED && event.id === id),
            take(1)
        ).subscribe(event => {
            if (event.payload.changed || fromCopy) {
                const config = this._getTileConfigurationByType(event.payload.type);
                this._initTile(event.id, { config: config, widget: widget });
            }
            this._postTileChange(event.id, widget, notGetLayoutFromServer, notShowToast, resetColumns, fromCopy);
        });
        this._setTileType(id, widget.tile.visualization.type);
    }

    private _postTileChange(id, widget, notGetLayoutFromServer, notShowToast, resetColumns, fromCopy) {
        if (widget.type === TamDashboardTileType.EDITOR) {
            this._reloadWidget(widget, [], false);
            return;
        }
        if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            !this._focusedEntity) {
            this._setTileNoData(widget);
            return;
        }

        this._toggleLoadingStatus(widget.id, true);
        let focusedEntity$;
        if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
            focusedEntity$ = this._queryService.prepareRelationshipFocusForQuery(this._focusedEntity.id,
                widget.tile.focus.relationshipTypes);
        } else {
            focusedEntity$ = this._queryService.prepareFocusForQuery(widget.tile, this._screenData.configuration.focus);
        }
        if (widget.tile.visualization.type === TamDashboardTileType.GRID) {
            focusedEntity$
                .pipe(take(1), map((entityIds) => {
                    if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && this._screenData.configuration.focus.includeSpecificEntityItself) {
                        entityIds = entityIds + ',' + this._screenData.configuration.focus.entityId;
                    } else if (widget.tile.focus.includeSpecificEntityItself) {
                        entityIds = entityIds + ',' + widget.tile.focus.entityId;
                    }
                    return entityIds;
                }), map((entityIds) => {
                    if (entityIds === '') {
                        this._setTileNoData(widget);
                        return;
                    }
                    this._refreshGrid(widget, entityIds, resetColumns, fromCopy, notGetLayoutFromServer);
                }))
                .subscribe(() => {
                    if (!notShowToast) {
                        this._showToast(widget.tile.tileName);
                    }
                });
        } else {
            focusedEntity$
                .pipe(take(1), map((entityIds) => {
                    if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && this._screenData.configuration.focus.includeSpecificEntityItself) {
                        entityIds = entityIds + ',' + this._screenData.configuration.focus.entityId;
                    } else if (widget.tile.focus.includeSpecificEntityItself) {
                        entityIds = entityIds + ',' + widget.tile.focus.entityId;
                    }
                    return entityIds;
                }))
                .subscribe((focusedEntityIds) => {
                    if (focusedEntityIds === '') {
                        this._setTileNoData(widget);
                        return;
                    }
                    this._reloadWidget(widget, focusedEntityIds, false);
                    if (!notShowToast) {
                        this._showToast(widget.tile.tileName);
                    }
                });
        }
    }

    private _saveGridLayout(dashboardId, tile) {
        this.dashboardOptions.actionSubject$.next({
            id: dashboardId,
            type: TamDashboardActions.SAVE_TILE_GRID_LAYOUT,
            payload: tile.id,
        });
    }

    private _setValueForFocus(focusInfo) {
        return this._entityService.getEntityListBySearchTextQuick(1, 10, focusInfo.shortName, this._source).pipe(
            map(response => this._entityService.mapEntityBriefList(response)),
            take(1),
            map((filteredEntityList) => {
                const index = filteredEntityList.findIndex(element => element.id === focusInfo.id);
                if (index !== -1) {
                    return focusInfo;
                }
                return null;
            })
        );
    }

    private _singleTileOperate(event) {
        if (event.payload.icon === TileIcons.REFRESH.icon) {
            const widget = this._screenData.configuration.widgets.filter(element => element.id === event.payload.id)[0];
            if (widget && widget.tile.visualization.type === TamDashboardTileType.GRID) {
                this._refreshSingleGridTile(event.payload.id);
            } else {
                this._refreshSingleTile(event.payload.id, true, true);
            }
        } else if (event.payload.icon === TileIcons.DELETE.icon) {
            this._deleteSingleTile(event.payload.id);
        } else if (event.payload.icon === TileIcons.COPY.icon) {
            this._copySingleTile(event.payload.id);
        }
    }

    private _setLineOrColumnChartLabel(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_CHART_LABEL_SETTING,
            payload: data,
        });
    }

    private _setPieChartLabel(id: string, displaySetting: string) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_PIR_CHART_DISPLAY,
            payload: displaySetting,
        });
    }

    private _setTileActive(id: string) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_ACTIVE
        });
    }

    private _setTileName(id: string, name: string) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_NAME,
            payload: name,
        });
    }

    private _setTileType(id: string, type: string) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_TYPE,
            payload: type,
        });
    }

    private _setTileData(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_DATA,
            payload: data,
        });
    }

    private _initTile(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.INIT_TILE,
            payload: data,
        });
    }

    private _loadData(id: string, tileConfig: any, focusedEntityIds: any) {
        // dashboard edit page tile get data use config not tileId
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.LOAD_DATA,
            payload: {
                tileConfig: tileConfig,
                focusedEntityIds: focusedEntityIds,
                useConfig: true
            },
        });
    }
    private _setActiveTile(operateTile: string = '') {
        this._setTileActive(this._activeTileId);
        const tileSetting = this._screenData.configuration.widgets.find(element => element.id === this._activeTileId);
        this._changeTile(tileSetting, {
            id: this._activeTileId,
            operateTile: operateTile
        });
    }

    private _showToast(message) {
        this._toast.notify({
            message: `${message} successfully updated.`,
            style: NotificationStyles.Success
        });
    }

    private _toggleLoadingStatus(id: string, isLoading: boolean) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_LOADING_STATUS,
            payload: isLoading
        });
    }

    private _setTileConfig(id: string, tileConfig: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_CONFIG,
            payload: tileConfig
        });
    }

    private _tileSettingEventHandler(event) {
        if (event.type === TileSettingEvents.preview) {
            // * only visualization type is TamDashboardTileType.GRID,
            // * and data.payload.visualization.type === TamDashboardTileType.GRID
            // * need get grid layout
            this._updateScreenData(event.id, event.payload);
            this._setTileConfig(event.id, event.payload);
            if (event.refreshTile) {
                if (this._activeVisualizationType === TamDashboardTileType.GRID &&
                    event.payload.visualization.type === TamDashboardTileType.GRID) {
                    this._refreshSingleGridTile(event.id, event.payload, true);
                } else {
                    this._refreshSingleTile(event.id);
                }
                this._activeVisualizationType = event.payload.visualization.type;
                this._updatePreviewStatus(event.id);
            }
        } else if (event.type === TileSettingEvents.addTile) {
            this.addTile(event.payload);
        } else if (event.type === TileSettingEvents.initTileSettings) {
            const config = this._getTileConfigurationByType(event.payload.visualization.type);
            const widget = this._screenData.configuration.widgets.filter(element => element.id === event.id)[0];
            widget.tile = event.payload;
            this._initTile(event.id, { config: config, widget: widget });
            this._refreshSingleTile(event.id);
        } else if (event.type === TileSettingEvents.clearActiveTile) {
            this._activeTileId = null;
            this._setTileActive(null);
        } else if (event.type === TileSettingEvents.changeTile) {
            this._changeTile(event.payload.tileSetting, event.payload.event);
        } else if (event.type === TileSettingEvents.deleteTile) {
            this.dashboardOptions.actionSubject$.next({
                id: event.payload,
                type: TamDashboardActions.DELETE_ITEM,
            });
        } else if (event.type === TileSettingEvents.pullTileSettings) {
            this._pullTileSettings(event);
        }
    }

    private _pullTileSettings(event) {
        this.dashboardOptions.actionSubject$.next({
            type: TamDashboardActions.PULL_TILE_SETTINGS,
            id: event.id,
            payload: event.payload
        });
    }

    private _updatePreviewStatus(id: string) {
        this._screenData.configuration.widgets.forEach(item => {
            if (item.id === id) {
                item.notPreview = false;
            }
        });
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_PREVIEW_STATUS,
            payload: false,
        });
    }

    private _updateScreenData(id: string, data: any) {
        this._screenData.configuration.widgets.forEach(item => {
            if (item.id === id) {
                item.tile = data;
            }
        });
    }

    private _updateScreenDataItemColumnWidthType(id: string) {
        this._screenData.configuration.widgets.forEach(item => {
            if (item.id === id && item.tile.visualization.settings.gridSetting) {
                item.tile.visualization.settings.gridSetting.automaticSizing = false;
            }
        });
    }

    private _refreshGrid(widget, focusedEntityIds, resetColumns = false, fromCopy = false, notGetLayoutFromServer = false) {
        if (fromCopy) {
            this._loadData(widget.id, widget.tile, focusedEntityIds);
        } else {
            // this._toggleLoadingStatus(widget.id, true);
            this.dashboardOptions.feedbackSubject$.pipe(
                filter((event) => event.id === widget.id && event.type === TamDashboardEvents.SET_CONFIG_FINISHED),
                take(1)
            ).subscribe(() => {
                this._loadData(widget.id, widget.tile, focusedEntityIds);
            });

            this.dashboardOptions.actionSubject$.next({
                id: widget.id,
                type: TamDashboardActions.LOAD_GRID_LAYOUT,
                payload: {
                    widget,
                    resetColumns,
                    notGetLayoutFromServer
                }
            });
        }
        return this.dashboardOptions.feedbackSubject$
            .pipe(filter((event) => event.id && event.id === widget.id && event.type === TamDashboardEvents.SET_DATA_FINISHED), take(1));
    }

    private _setTileNoData(element) {
        // except textarea tile which is not affected by focus entity, other tiles show no data msg to user
        if (element.tile.visualization.type !== TamDashboardTileType.EDITOR) {
            this._setTileData(element.id, null);
        }
    }

    private _returnLatestSettings(event) {
        this.tileSettingConfig.actionSubject$.next({
            type: TamTileSettingsEvents.RETURN_LATEST_SETTINGS,
            payload: event.payload,
            refreshTile: event.refreshTile
        });
    }
}
